/**
 * IssueDetail
 * Expandable panel showing full issue details, status actions,
 * comments timeline, photos, and affected bookings.
 * Used in both the Issues list page and Inventory Maintenance tab.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check, X as XIcon, Wrench, Package, RotateCcw,
  MessageSquare, Send, Loader2, Image as ImageIcon, Calendar, Pencil
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import {
  getIssueById, confirmIssue, dismissIssue, assignRepair,
  resolveIssue, closeIssue, reopenIssue, addComment,
  getIssuePhotoUrl, updateIssue, uploadIssuePhoto,
  type IssueWithRelations,
} from '../services/issues.service';
import { getUsers, type UserWithRole } from '../services/users.service';
import { useAuth } from '../contexts/AuthContext';
import IssueAffectedBookings from './IssueAffectedBookings';

interface IssueDetailProps {
  issueId: string;
  merchantId: string;
  onStatusChanged?: () => void;
  defaultExpanded?: boolean;
}

export default function IssueDetail({
  issueId,
  merchantId,
  onStatusChanged,
  defaultExpanded = false,
}: IssueDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { formatDate, formatDateTimeCompact } = useDateFormatter();
  const { accessControl } = useAuth();
  const [issue, setIssue] = useState<IssueWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded] = useState(defaultExpanded);
  const [actionLoading, setActionLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Action form states
  const [showConfirmForm, setShowConfirmForm] = useState(false);
  const [confirmNotes, setConfirmNotes] = useState('');
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeType, setAssigneeType] = useState<'internal' | 'external'>('internal');
  const [assignedToUserId, setAssignedToUserId] = useState<string>('');
  const [repairNotes, setRepairNotes] = useState('');
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionType, setResolutionType] = useState<'repaired' | 'replaced_part' | 'retired_item' | 'no_action_needed' | 'cannot_repair'>('repaired');
  const [resetCheckCounter, setResetCheckCounter] = useState(false);

  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Photo upload
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Editable due date
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');

  useEffect(() => {
    if (expanded) loadIssue();
    loadUserId();
    loadTeamMembers();
  }, [issueId, expanded]);

  async function loadUserId() {
    const { data } = await supabase.auth.getUser();
    setUserId(data?.user?.id || null);
  }

  async function loadTeamMembers() {
    const { data } = await getUsers(accessControl);
    if (data) setTeamMembers(data);
  }

  async function loadIssue() {
    setLoading(true);
    const { data } = await getIssueById(issueId);
    if (data) {
      setIssue(data);
      setResetCheckCounter(data.category === 'scheduled_check');
    }
    setLoading(false);
  }

  async function handleConfirm() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await confirmIssue(issueId, userId, confirmNotes);
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else { showToast(t('issueDetail.issueConfirmed'), 'success'); setShowConfirmForm(false); }
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleDismiss() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await dismissIssue(issueId, userId, confirmNotes);
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else showToast(t('issueDetail.issueDismissed'), 'success');
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleAssign() {
    if (!userId || !assigneeName.trim()) return;
    setActionLoading(true);
    const { error } = await assignRepair(issueId, userId, {
      assignee_type: assigneeType,
      assignee_name: assigneeName.trim(),
      assigned_to_user_id: assigneeType === 'internal' && assignedToUserId ? assignedToUserId : null,
      repair_notes: repairNotes || undefined,
    });
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else { showToast(t('issueDetail.repairAssigned'), 'success'); setShowAssignForm(false); }
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleResolve() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await resolveIssue(issueId, userId, {
      resolution_notes: resolutionNotes || undefined,
      resolution_type: resolutionType,
      reset_check_counter: resetCheckCounter,
    });
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else { showToast(t('issueDetail.issueResolved'), 'success'); setShowResolveForm(false); }
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleClose() {
    setActionLoading(true);
    const { error } = await closeIssue(issueId, userId || undefined);
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else showToast(t('issueDetail.issueClosed'), 'success');
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleReopen() {
    if (!userId) return;
    setActionLoading(true);
    const { error } = await reopenIssue(issueId, userId);
    if (error) showToast(t('issueDetail.failed') + ': ' + error, 'error');
    else showToast(t('issueDetail.issueReopened'), 'success');
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  async function handleAddComment() {
    if (!userId || !newComment.trim()) return;
    setAddingComment(true);
    const { error } = await addComment(issueId, userId, newComment.trim());
    if (error) showToast(t('issueDetail.failedToAddComment'), 'error');
    else setNewComment('');
    await loadIssue();
    setAddingComment(false);
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    e.target.value = '';
    setUploadingPhoto(true);
    const { error } = await uploadIssuePhoto(issueId, merchantId, userId, file);
    if (error) showToast(t('issueDetail.failedToUploadPhoto') + ': ' + error, 'error');
    else showToast(t('issueDetail.photoAdded'), 'success');
    await loadIssue();
    setUploadingPhoto(false);
  }

  async function handleSaveDueDate() {
    setActionLoading(true);
    const { error } = await updateIssue(issueId, {
      repair_due_date: dueDateValue || null,
    });
    if (error) showToast(t('issueDetail.failedToUpdateDueDate'), 'error');
    else showToast(dueDateValue ? t('issueDetail.dueDateUpdated') : t('issueDetail.dueDateRemoved'), 'success');
    setEditingDueDate(false);
    await loadIssue();
    onStatusChanged?.();
    setActionLoading(false);
  }

  function handleBlockingChanged(newBlockingTimeId: string | null) {
    if (issue) {
      setIssue({ ...issue, blocking_time_id: newBlockingTimeId });
    }
  }

  const timeAgo = (d: string) => formatDateTimeCompact(d);

  if (!expanded) return null;

  if (loading) {
    return (
      <div className="p-lg flex items-center gap-sm text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('issueDetail.loading')}
      </div>
    );
  }

  if (!issue) return null;

  return (
    <div className="border-t border-border p-lg space-y-lg">
      {/* Description */}
      {issue.description && (
        <div>
          <p className="text-sm text-secondary mb-xs">{t('issueDetail.description')}</p>
          <p className="text-sm text-primary">{issue.description}</p>
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-md text-sm">
        {issue.inventory_items && (
          <div>
            <p className="text-secondary text-xs flex items-center gap-xs">
              <Package className="w-3 h-3" />
              {t('issueDetail.inventoryItem')}
            </p>
            <p className="text-primary font-medium">
              {issue.inventory_items.item_number}
              {issue.inventory_items.size && (
                <span className="text-secondary font-normal ml-md">({issue.inventory_items.size})</span>
              )}
            </p>
          </div>
        )}
        {issue.bookings && (
          <div>
            <p className="text-secondary text-xs">{t('issueDetail.booking')}</p>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${issue.booking_id}/edit`); }}
              className="text-primary font-medium text-blue hover:underline"
            >
              #{issue.bookings.order_id}
            </button>
          </div>
        )}
        {issue.assignee_name && (
          <div>
            <p className="text-secondary text-xs">{t('issueDetail.assignedTo')}</p>
            <p className="text-primary font-medium">{issue.assignee_name} ({issue.assignee_type})</p>
          </div>
        )}
        {issue.repair_notes && (
          <div>
            <p className="text-secondary text-xs">{t('issueDetail.repairInstructions')}</p>
            <p className="text-primary text-sm whitespace-pre-wrap">{issue.repair_notes}</p>
          </div>
        )}
        {issue.status !== 'closed' ? (
          <div>
            <p className="text-secondary text-xs flex items-center gap-xs">
              <Calendar className="w-3 h-3" />
              {t('issueDetail.dueDate')}
            </p>
            {editingDueDate ? (
              <div className="flex items-center gap-xs mt-xs">
                <input
                  type="date"
                  value={dueDateValue}
                  onChange={(e) => setDueDateValue(e.target.value)}
                  className="input text-sm py-0.5 px-sm"
                />
                <button
                  onClick={handleSaveDueDate}
                  className="btn-icon text-success"
                  disabled={actionLoading}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingDueDate(false)}
                  className="btn-icon text-secondary"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setDueDateValue(issue.repair_due_date || '');
                  setEditingDueDate(true);
                }}
                className="flex items-center gap-xs group"
              >
                {issue.repair_due_date ? (
                  <span className={`font-medium ${new Date(issue.repair_due_date) < new Date() ? 'text-danger' : 'text-primary'}`}>
                    {formatDate(issue.repair_due_date)}
                  </span>
                ) : (
                  <span className="text-secondary text-sm">{t('issueDetail.notSet')}</span>
                )}
                <Pencil className="w-3 h-3 text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
        ) : issue.repair_due_date ? (
          <div>
            <p className="text-secondary text-xs">{t('issueDetail.dueDate')}</p>
            <p className="font-medium text-primary">
              {formatDate(issue.repair_due_date)}
            </p>
          </div>
        ) : null}
        {issue.confirmation_notes && (
          <div className="col-span-2 sm:col-span-3">
            <p className="text-secondary text-xs">{t('issueDetail.confirmationNotes')}</p>
            <p className="text-primary whitespace-pre-wrap">{issue.confirmation_notes}</p>
          </div>
        )}
        {issue.resolution_notes && (
          <div className="col-span-2 sm:col-span-3">
            <p className="text-secondary text-xs">{t('issueDetail.resolution')}</p>
            <p className="text-primary whitespace-pre-wrap">{issue.resolution_notes} ({issue.resolution_type})</p>
          </div>
        )}
      </div>

      {/* Photos */}
      {issue.issue_photos && issue.issue_photos.length > 0 && (
        <div>
          <p className="text-sm text-secondary mb-xs flex items-center gap-xs">
            <ImageIcon className="w-4 h-4" /> {t('issueDetail.photos')} ({issue.issue_photos.length})
          </p>
          <div className="flex flex-wrap gap-sm">
            {issue.issue_photos.map((photo) => (
              <a
                key={photo.id}
                href={getIssuePhotoUrl(photo.file_path)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 h-20 rounded-lg overflow-hidden border border-border hover:border-blue transition-colors"
              >
                <img
                  src={getIssuePhotoUrl(photo.file_path)}
                  alt={photo.file_name}
                  className="w-full h-full object-cover"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Status actions */}
      <div className="space-y-md">
        <p className="text-sm font-medium text-primary">{t('issueDetail.actions')}</p>
        <div className="flex flex-wrap gap-sm">
          {issue.status === 'reported' && (
            <>
              <button onClick={() => setShowConfirmForm(!showConfirmForm)} className="btn btn-sm btn-ghost text-blue hover:bg-blue/10" disabled={actionLoading}>
                <Check className="w-4 h-4" /> {t('issueDetail.confirm')}
              </button>
              <button onClick={handleDismiss} className="btn btn-sm btn-ghost text-secondary" disabled={actionLoading}>
                <XIcon className="w-4 h-4" /> {t('issueDetail.dismiss')}
              </button>
            </>
          )}
          {['confirmed', 'reported'].includes(issue.status) && (
            <button onClick={() => setShowAssignForm(!showAssignForm)} className="btn btn-sm btn-ghost text-warning hover:bg-warning/10" disabled={actionLoading}>
              <Wrench className="w-4 h-4" /> {t('issueDetail.assignRepair')}
            </button>
          )}
          {issue.status === 'repair_assigned' && (
            <button onClick={() => {
              setAssigneeName(issue.assignee_name || '');
              setAssigneeType((issue.assignee_type as 'internal' | 'external') || 'internal');
              setAssignedToUserId(issue.assigned_to_user_id || '');
              setRepairNotes(issue.repair_notes || '');
              setShowAssignForm(!showAssignForm);
            }} className="btn btn-sm btn-ghost text-warning hover:bg-warning/10" disabled={actionLoading}>
              <Wrench className="w-3.5 h-3.5" /> {t('issueDetail.reassign')}
            </button>
          )}
          {['repair_assigned', 'confirmed'].includes(issue.status) && (
            <button onClick={() => setShowResolveForm(!showResolveForm)} className="btn btn-sm btn-ghost text-success hover:bg-success/10" disabled={actionLoading}>
              <Check className="w-4 h-4" /> {t('issueDetail.resolve')}
            </button>
          )}
          {issue.status === 'resolved' && (
            <button onClick={handleClose} className="btn btn-sm btn-ghost text-secondary" disabled={actionLoading}>
              <Check className="w-4 h-4" /> {t('issueDetail.closeIssue')}
            </button>
          )}
          {['resolved', 'cannot_repair', 'closed', 'dismissed'].includes(issue.status) && (
            <button onClick={handleReopen} className="btn btn-sm btn-ghost text-secondary" disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4" /> {t('issueDetail.reopen')}</>}
            </button>
          )}
        </div>

        {/* Confirm form */}
        {showConfirmForm && (
            <div className="p-md border border-border rounded-lg space-y-sm">
              <textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                className="input w-full"
                rows={2}
                placeholder={t('issueDetail.confirmationNotesPlaceholder')}
              />
              <div className="flex gap-sm">
                <button onClick={handleConfirm} className="btn btn-sm btn-primary" disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('issueDetail.confirmIssue')}
                </button>
                <button onClick={() => setShowConfirmForm(false)} className="btn btn-sm btn-ghost">{t('common.cancel')}</button>
              </div>
            </div>
          )}

          {/* Assign form */}
          {showAssignForm && (
            <div className="p-md border border-border rounded-lg space-y-sm">
              <div>
                <label className="text-xs text-secondary">{t('issueDetail.type')}</label>
                <select
                  value={assigneeType}
                  onChange={(e) => {
                    const type = e.target.value as 'internal' | 'external';
                    setAssigneeType(type);
                    if (type === 'external') { setAssignedToUserId(''); }
                    else { setAssigneeName(''); }
                  }}
                  className="input w-full text-sm"
                >
                  <option value="internal">{t('issueDetail.internalTeamMember')}</option>
                  <option value="external">{t('issueDetail.external')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-secondary">{assigneeType === 'internal' ? t('issueDetail.teamMember') : t('issueDetail.name')} *</label>
                {assigneeType === 'internal' ? (
                  <select
                    value={assignedToUserId}
                    onChange={(e) => {
                      const uid = e.target.value;
                      setAssignedToUserId(uid);
                      const member = teamMembers.find(m => m.id === uid);
                      setAssigneeName(member?.full_name || '');
                    }}
                    className="input w-full text-sm"
                  >
                    <option value="">{t('issueDetail.selectTeamMember')}</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.full_name || '(no name)'} — {m.role}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={assigneeName} onChange={(e) => setAssigneeName(e.target.value)} className="input w-full text-sm" placeholder={t('issueDetail.personOrCompany')} />
                )}
              </div>
              <textarea value={repairNotes} onChange={(e) => setRepairNotes(e.target.value)} className="input w-full text-sm" rows={2} placeholder={t('issueDetail.repairInstructionsPlaceholder')} />
              <div className="flex gap-sm">
                <button onClick={handleAssign} className="btn btn-sm btn-primary" disabled={actionLoading || !assigneeName.trim()}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('issueDetail.assign')}
                </button>
                <button onClick={() => setShowAssignForm(false)} className="btn btn-sm btn-ghost">{t('common.cancel')}</button>
              </div>
            </div>
          )}

          {/* Resolve form */}
          {showResolveForm && (
            <div className="p-md border border-border rounded-lg space-y-sm">
              <div>
                <label className="text-xs text-secondary">{t('issueDetail.resolutionType')}</label>
                <select value={resolutionType} onChange={(e) => setResolutionType(e.target.value as any)} className="input w-full text-sm">
                  <option value="repaired">{t('issueDetail.repaired')}</option>
                  <option value="replaced_part">{t('issueDetail.replacedPart')}</option>
                  <option value="retired_item">{t('issueDetail.retiredItem')}</option>
                  <option value="no_action_needed">{t('issueDetail.noActionNeeded')}</option>
                  <option value="cannot_repair">{t('issueDetail.cannotRepair')}</option>
                </select>
              </div>
              <textarea value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} className="input w-full text-sm" rows={2} placeholder={t('issueDetail.whatWasDone')} />
              {issue.inventory_item_id && (
                <label className="flex items-center gap-sm text-sm">
                  <input
                    type="checkbox"
                    checked={resetCheckCounter}
                    onChange={(e) => setResetCheckCounter(e.target.checked)}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  {t('issueDetail.resetCheckCounter')}
                  <span className="text-xs text-secondary">({t('issueDetail.itemFullyInspected')})</span>
                </label>
              )}
              <div className="flex gap-sm">
                <button onClick={handleResolve} className="btn btn-sm btn-primary" disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('issueDetail.markResolved')}
                </button>
                <button onClick={() => setShowResolveForm(false)} className="btn btn-sm btn-ghost">{t('common.cancel')}</button>
              </div>
            </div>
          )}
      </div>

      {/* Affected bookings (only for item-linked issues) */}
      {issue.inventory_item_id && userId && issue.status !== 'closed' && (
        <IssueAffectedBookings
          issueId={issue.id}
          inventoryItemId={issue.inventory_item_id}
          productTypeId={issue.product_type_id || issue.inventory_items?.product_type_id || null}
          productName={issue.product_types?.product_name}
          itemNumber={issue.inventory_items?.item_number || null}
          itemSize={issue.inventory_items?.size || null}
          merchantId={merchantId}
          blockingTimeId={issue.blocking_time_id}
          repairDueDate={issue.repair_due_date}
          userId={userId}
          onBlockingChanged={handleBlockingChanged}
        />
      )}

      {/* Comments timeline */}
      <div className="space-y-sm">
        <p className="text-sm font-medium text-primary flex items-center gap-xs">
          <MessageSquare className="w-4 h-4" />
          {t('issueDetail.activity')} ({issue.issue_comments?.length || 0})
        </p>
        <div className="space-y-sm max-h-64 overflow-y-auto">
          {(issue.issue_comments || []).map((comment) => (
            <div key={comment.id} className={`text-sm p-sm rounded-lg ${comment.is_system ? 'bg-secondary/5 text-secondary italic' : 'bg-secondary/10'}`}>
              {comment.profiles && !comment.is_system && (
                <p className="text-xs font-medium text-primary mb-0.5">{comment.profiles.full_name}</p>
              )}
              <p className={comment.is_system ? '' : 'text-primary'}>{comment.comment}</p>
              <p className="text-xs text-secondary mt-0.5">
                {comment.is_system && comment.profiles && (
                  <span>by {comment.profiles.full_name} · </span>
                )}
                {timeAgo(comment.created_at)}
              </p>
            </div>
          ))}
        </div>

        {/* Add comment */}
        <div className="flex gap-sm">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
            className="input flex-1 text-sm"
            placeholder={t('issueDetail.addCommentPlaceholder')}
          />
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
          <button onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="btn btn-sm btn-ghost" title={t('issueDetail.addPhoto')}>
            {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </button>
          <button onClick={handleAddComment} disabled={addingComment || !newComment.trim()} className="btn btn-sm btn-primary">
            {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
